import React, { useEffect, useState } from "react";
import i18n from "i18n";
import { Grid, Link, Tooltip, Typography, withStyles } from "@material-ui/core";
import { Info } from "@material-ui/icons";
import { CompositionStore } from "modules/composition/CompositionStore";
import { FirebaseDocument } from "common/models/FirebaseDocument";
import { BenchCompositionFile } from "modules/composition/models/BenchCompositionFile";
import { DisplayValue, SectionTitle } from "./Components";

const styles = {
  tooltipContainer: { width: 190, padding: "0.25rem 0.5rem", gap: "0.3125rem" },
  tooltipTitle: { fontWeight: 700, fontSize: 14, color: "#FFFFFF" },
  tooltipValue: { fontSize: 12, color: "#FFFFFF" },
  icon: {
    width: "1rem",
    height: "1rem",
    color: "#FFFFFF",
    paddingTop: 2,
  },
  link: { color: "#137CBD", display: "block", cursor: "pointer" },
};

const ButtonInfo = ({
  classes,
  scope,
  additionalInformation,
}: {
  classes: any;
  scope: string;
  additionalInformation: string;
}) => (
  <Tooltip
    title={
      <Grid container className={classes.tooltipContainer}>
        <Grid item>
          <Typography className={classes.tooltipTitle}>
            {i18n.project.rightSideMenu.costOrigin.scope}
          </Typography>
          <Typography className={classes.tooltipValue}>{scope}</Typography>
        </Grid>
        <Grid item>
          <Typography className={classes.tooltipTitle}>
            {i18n.project.rightSideMenu.costOrigin.additionalInformation}
          </Typography>
          <Typography className={classes.tooltipValue}>
            {additionalInformation}
          </Typography>
        </Grid>
      </Grid>
    }
    style={{ opacity: 1 }}
  >
    <Typography>
      <Info className={classes.icon} />
    </Typography>
  </Tooltip>
);

interface IProps {
  classes: any;
  /** ID do banco de composições. Usado para obter suas informações */
  rateLibraryId: string;
  /** Nome do insumo/composição no banco de composições originário. */
  nameOnRateLibrary: string;
  /** Se a linha selecionada é uma composição. */
  isTask: boolean;
}

/** Componente Seção de Origem do Custo da linha.
 * Este componente exibe os dados formatados do banco de composições
 * originário das informações da linha.
 */
const CostOriginProperties = ({
  classes,
  rateLibraryId,
  nameOnRateLibrary = "",
  isTask,
}: IProps) => {
  const maxTextLength = 100;
  const [bench, setBench] = useState<
    FirebaseDocument<BenchCompositionFile> | undefined
  >(CompositionStore.benchComposition);
  const [showAllText, setShowAllText] = useState(false);

  const suggestions = {
    BR: "Brasil",
    AC: "Acre",
    AL: "Alagoas",
    AP: "Amapá",
    AM: "Amazonas",
    BA: "Bahia",
    CE: "Ceará",
    DF: "Distrito Federal",
    ES: "Espírito Santo",
    GO: "Goiás",
    MA: "Maranhão",
    MT: "Mato Grosso",
    MS: "Mato Grosso do Sul",
    MG: "Minas Gerais",
    PA: "Pará",
    PB: "Paraíba",
    PR: "Paraná",
    PE: "Pernambuco",
    PI: "Piauí",
    RJ: "Rio de Janeiro",
    RN: "Rio Grande do Norte",
    RS: "Rio Grande do Sul",
    RO: "Rondônia",
    RR: "Roraima",
    SC: "Santa Catarina",
    SP: "São Paulo",
    SE: "Sergipe",
    TO: "Tocantins",
  };

  useEffect(() => {
    // Usado para diminuir o volume de informações no menu
    setShowAllText(nameOnRateLibrary.length <= maxTextLength);
  }, [nameOnRateLibrary]);

  useEffect(() => {
    if (rateLibraryId) {
      // Se for diferente do que já está em memória
      if (CompositionStore.lastIdLoaded !== rateLibraryId) {
        CompositionStore.getBenchCompositionFile(rateLibraryId).then(() => {
          const newBench = CompositionStore.benchComposition;
          if (!bench || bench.id !== newBench.id) setBench(newBench);
        });
      }
    } else {
      // Se não tiver ID do banco, limpa a variável
      if (bench !== undefined) setBench(undefined);
    }
  }, [rateLibraryId]);

  if (!bench || !bench.data) return <></>;

  const location = bench.data.location
    ? bench.data.location.map((el) => `${suggestions[el] || ""}`).join(",")
    : "";

  return (
    <Grid
      data-testid="grid-CostOriginProperties"
      style={{ display: "inline-block" }}
    >
      <SectionTitle>{i18n.project.rightSideMenu.origin}</SectionTitle>
      <DisplayValue
        label={i18n.project.rightSideMenu.costOrigin.database}
        value={
          <Grid container>
            <Typography style={{ width: "calc(100% - 17px)" }}>
              {bench.data.name}
            </Typography>
            <ButtonInfo
              classes={classes}
              scope={location}
              additionalInformation={bench.data.description || "-"}
            />
          </Grid>
        }
      />
      <DisplayValue
        label={
          isTask
            ? i18n.project.rightSideMenu.costOrigin.composition
            : i18n.project.rightSideMenu.costOrigin.resource
        }
        value={
          <>
            {showAllText
              ? nameOnRateLibrary
              : `${nameOnRateLibrary.substring(0, maxTextLength)}...`}
            {!showAllText && (
              <Link
                className={classes.link}
                underline="always"
                onClick={() => setShowAllText(true)}
              >
                {`${i18n.seeMore}.`}
              </Link>
            )}
          </>
        }
      />
    </Grid>
  );
};

export default withStyles(styles)(CostOriginProperties);



// WEBPACK FOOTER //
// ./src/modules/projectRightMenu/components/layouts/RowProperties/CostOriginProperties.tsx